import { isEmpty } from "lodash-es";
import Wizard from "../../components/modal/Wizard";
import SearchBar from "../../components/searchBar/SearchBar";
import { SingleSelect } from "../../components/menu/Select";
import { Dialog, RadioGroup } from "@ark-ui/solid";
import { createMemo, createSignal, For, Show } from "solid-js";
import type { LanguageCode } from "../../state/i18n";
import { useTranslation } from "../../state/i18n";
import type FolderId from "../../state/FolderId";
import type CustomerId from "../../state/CustomerId";
import { useUserState } from "../../state/user";
import ApiService from "../../services/ApiService.ts";
import type { TemplateFilter } from "./templateUtil.ts";
import { getAvailableTemplates, templateFilters } from "./templateUtil.ts";
import Templates from "./Templates.tsx";

const availableLanguages = ["en", "nl", "es", "de", "fr", "pt"];

type CreateFormWizardProps = {
  customerId: CustomerId;
  folderId: FolderId;
};

const CreateFormWizard = (props: CreateFormWizardProps) => {
  const { t } = useTranslation();
  const user = useUserState();

  const [activeFilter, setActiveFilter] = createSignal<TemplateFilter>("ALL");
  const [query, setQuery] = createSignal<string>("");
  const [language, setLanguage] = createSignal<LanguageCode>(
    availableLanguages.includes(user().language) ? user().language : "en",
  );

  const templates = ApiService.getTemplates(language);

  const availableFilters = createMemo(() =>
    templateFilters.filter((category) => !isEmpty(getAvailableTemplates(templates.data, category.value, query()))),
  );

  return (
    <Wizard
      title={t(($) => $.TEMPLATES_MODAL_TITLE)}
      content={() => (
        <Dialog.Context>
          {(context) => (
            <>
              <div class="flex flex-col gap-y-4 pt-4 pb-2">
                <div class="grid grid-cols-4 items-center justify-between gap-x-2 px-4">
                  <div class="col-span-3 w-full">
                    <SearchBar
                      placeholder={t(($) => $.TEMPLATES_SEARCH_PLACEHOLDER)}
                      query={query()}
                      onQueryChange={setQuery}
                    />
                  </div>
                  <SingleSelect<LanguageCode>
                    ariaLabel={t(($) => $.TEMPLATES_LANGUAGE_SELECT_ARIA)}
                    hasError={false}
                    onSelect={setLanguage}
                    value={language()}
                    options={[
                      { variant: "text", label: t(($) => $.TEMPLATES_LANGUAGE_ENGLISH), value: "en" },
                      { variant: "text", label: t(($) => $.TEMPLATES_LANGUAGE_DUTCH), value: "nl" },
                      { variant: "text", label: t(($) => $.TEMPLATES_LANGUAGE_SPANISH), value: "es" },
                      { variant: "text", label: t(($) => $.TEMPLATES_LANGUAGE_GERMAN), value: "de" },
                      { variant: "text", label: t(($) => $.TEMPLATES_LANGUAGE_FRENCH), value: "fr" },
                      { variant: "text", label: t(($) => $.TEMPLATES_LANGUAGE_PORTUGUESE), value: "pt" },
                    ]}
                    sameWidth={false}
                  />
                </div>
                <Show when={!isEmpty(availableFilters())}>
                  <RadioGroup.Root
                    value={activeFilter()}
                    onValueChange={(e) => setActiveFilter(e.value as TemplateFilter)}
                    class="flex flex-wrap gap-2 px-4"
                  >
                    <For each={availableFilters()}>
                      {(category) => (
                        <RadioGroup.Item
                          value={category.value}
                          class="relative min-w-0 rounded-sm border border-subtle bg-surface-default px-2.5 py-1.5 text-base-medium text-primary select-none checked:border-active checked:bg-surface-active-brand checked:text-active focussed:outline-default"
                        >
                          <RadioGroup.ItemText class="truncate">{t(category.translationKey)}</RadioGroup.ItemText>
                          <RadioGroup.ItemControl />
                          <RadioGroup.ItemHiddenInput />
                        </RadioGroup.Item>
                      )}
                    </For>
                  </RadioGroup.Root>
                </Show>
              </div>

              <div class="overflow-y-auto p-4">
                <Templates
                  onboarding={false}
                  query={query()}
                  activeFilter={activeFilter()}
                  language={language()}
                  folderId={props.folderId}
                  onSelectSuccess={() => context().setOpen(false)}
                />
              </div>
            </>
          )}
        </Dialog.Context>
      )}
    />
  );
};

export default CreateFormWizard;
